import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, ShoppingCart, RefreshCw, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface CheckoutErrorScreenProps {
  error?: Error;
  onRetry?: () => void;
}

const CheckoutErrorScreen: React.FC<CheckoutErrorScreenProps> = ({ 
  error, 
  onRetry 
}) => {
  const navigate = useNavigate();

  const handleBackToCart = () => {
    // Clean up any session storage flags
    sessionStorage.removeItem('checkout-flow-initiated');
    sessionStorage.removeItem('payment-flow-initiated');
    localStorage.removeItem('pendingOrderUpdate');
    
    navigate('/cart');
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      // Fallback: reload the page
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating Geometric Shapes */}
        <div className="absolute top-20 left-20 w-16 h-16 bg-gradient-to-br from-jamaica-green/10 to-jamaica-green/5 rounded-2xl transform rotate-45 animate-pulse delay-300"></div>
        <div className="absolute top-40 right-32 w-12 h-12 bg-jamaica-yellow/15 rounded-full animate-bounce delay-500"></div>
        <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-br from-jamaica-yellow/8 to-jamaica-green/8 rounded-3xl transform rotate-12 animate-ping delay-700"></div>
        <div className="absolute bottom-20 right-20 w-14 h-14 bg-jamaica-green/12 rounded-lg transform -rotate-12 animate-pulse delay-900"></div>
        
        {/* Diagonal Lines Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(45deg, rgba(0, 116, 47, 0.1) 1px, transparent 1px),
              linear-gradient(-45deg, rgba(255, 201, 1, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '80px 80px, 120px 120px',
            backgroundPosition: '0 0, 40px 40px'
          }}></div>
        </div>
        
        {/* Gradient Blobs */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-64 h-64 bg-jamaica-green/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-jamaica-yellow/10 rounded-full blur-2xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
      </div>

      {/* Main Content */}
      <div className="max-w-md w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative backdrop-blur-sm bg-white/80 rounded-2xl shadow-xl border border-white/40 p-8 text-center"
        >
          {/* Animated Error Icon */}
          <motion.div
            className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-full mb-6 shadow-lg"
            animate={{ 
              scale: [1, 1.05, 1],
              rotate: [0, 2, -2, 0] 
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <AlertCircle className="w-8 h-8 text-white" />
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-2xl font-bold text-gray-900 mb-4"
          >
            Checkout Error
          </motion.h1>

          {/* Message */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-gray-600 mb-6 leading-relaxed"
          >
            Something went wrong during checkout. Don't worry - your cart items are still saved.
          </motion.p>

          {/* Error Details (in development) */}
          {import.meta.env.DEV && error && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="bg-red-50 border border-red-100 rounded-lg p-3 mb-6 text-left"
            >
              <p className="text-xs text-red-600 font-mono break-all">
                {error.message}
              </p>
            </motion.div>
          )}

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="space-y-3"
          >
            {/* Primary Action: Back to Cart */}
            <Button
              onClick={handleBackToCart}
              className="w-full bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white font-medium py-3 px-6 rounded-lg hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
              size="lg"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Cart
            </Button>

            {/* Secondary Action: Try Again */}
            <Button
              onClick={handleRetry}
              variant="outline"
              className="w-full border-jamaica-green/20 text-jamaica-green hover:bg-jamaica-green/5 flex items-center justify-center gap-2"
              size="lg"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </Button>
          </motion.div>

          {/* Help Text */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="bg-gray-50 rounded-lg p-4 border mt-6"
          >
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600 mb-2">
              <ShoppingCart className="w-4 h-4" />
              <span className="font-medium">Your cart is safe</span>
            </div>
            <p className="text-xs text-gray-500">
              All your items are still in your cart. Try refreshing or go back to cart to start over.
            </p>
          </motion.div>
        </motion.div>

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          className="text-center text-xs text-gray-400 mt-6"
        >
          If this problem persists, please contact support
        </motion.p>
      </div>
    </div>
  );
};

export default CheckoutErrorScreen;